/**
 * The twelve western reaction stickers — the slot, before the art.
 *
 * ============================ WHY THIS IS EMPTY ================================
 * `ART-PROMPTS-2026-09-05.md` §6 has the twelve prompts and Tim generates them; as of
 * this phase nothing has landed in `incoming/`. So this ships the PIPELINE and not a
 * placeholder: `public/art/stickers/stickers.json` is a real manifest with an empty
 * `stickers` array, `sync-art.mjs` fills it the moment the renders appear, and the
 * tray shows the emoji row alone until then.
 *
 * No placeholder stickers are drawn, deliberately. A grey square with a hat on it is
 * the kind of thing that gets shipped and then lived with for a year, and a taunt tray
 * that offers eight emoji and nothing else is not broken — it is a tray with eight
 * things in it.
 *
 * The manifest is fetched ONCE, lazily, the first time anything asks. A failed fetch is
 * an empty set, not an exception: the tray is a nice-to-have and a 404 on a JSON file
 * must not take a table down.
 * ===============================================================================
 */

export interface Sticker {
  /** `sticker-01-tiphat` … `sticker-12-coffinnail`, from the prompt sheet. */
  id: string;
  /** What the tray's button says, and what a screen reader reads. */
  label: string;
  /** Path under `/art/stickers/`, written by `sync-art.mjs`. */
  file: string;
}

export interface StickerManifest {
  /** Bumped by `sync-art.mjs` when the shape changes, never by hand. */
  version: number;
  stickers: Sticker[];
}

export const EMPTY_MANIFEST: StickerManifest = { version: 1, stickers: [] };

const URL_PATH = "/art/stickers/stickers.json";

let cache: StickerManifest | null = null;
let inflight: Promise<StickerManifest> | null = null;

/** The set as it stands right now. Empty until `loadStickers` has resolved. */
export function stickersNow(): readonly Sticker[] {
  return cache?.stickers ?? [];
}

export async function loadStickers(): Promise<StickerManifest> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(URL_PATH, { cache: "force-cache" });
      if (!res.ok) throw new Error(String(res.status));
      const raw = (await res.json()) as unknown;
      cache = parseManifest(raw);
    } catch {
      cache = EMPTY_MANIFEST;
    }
    inflight = null;
    return cache;
  })();
  return inflight;
}

/**
 * Field by field, because this is a file on a CDN and the code has to survive an older
 * or newer one. Anything that is not a complete sticker is dropped rather than drawn.
 */
export function parseManifest(raw: unknown): StickerManifest {
  const o = raw as Partial<StickerManifest> | null;
  const list = Array.isArray(o?.stickers) ? o!.stickers : [];
  const stickers = list.filter((s): s is Sticker =>
    !!s && typeof s.id === "string" && typeof s.label === "string" && typeof s.file === "string"
    && s.id.length > 0 && s.file.length > 0);
  return { version: typeof o?.version === "number" ? o!.version : 1, stickers };
}

/** The URL of one sticker, or null when the render has not landed. */
export function stickerUrl(id: string | undefined): string | null {
  if (!id) return null;
  const s = stickersNow().find((x) => x.id === id);
  return s ? `/art/stickers/${s.file}` : null;
}
